import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Helmet } from "react-helmet";
import CountUp from "react-countup";
import { motion } from "framer-motion";

  const placementStats = [
    { number: 1200, label: "Students Placed" },
    { number: 300, label: "Hiring Partners" },
    { number: 95, label: "Placement Success Rate" },
    { number: 12, label: "Years of Placement Support" },
  ];
/* -------------------- Styled Components -------------------- */

const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 70px 20px;
`;

const Heading = styled.h1`
  text-align: center;
  font-size: 2.4rem;
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: 12px;
`;

const SubHeading = styled.p`
  text-align: center;
  font-size: 1rem;
  color: ${(props) => props.theme.colors.medium};
  margin-bottom: 60px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

/* -------------------- Stats -------------------- */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 32px;
  margin-top: 50px;
  align-items: stretch;
`;


const StatCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 34px 24px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 160px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-6px);
  }
`;

const StatNumber = styled.h4`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: 10px;
  line-height: 1.1;
`;


const StatLabel = styled.p`
  font-size: 0.95rem;
  color: #555;
  max-width: 180px;
`;


/* -------------------- Companies (Infinite Scroll) -------------------- */

const CompaniesHeading = styled.h3`
  text-align: center;
  font-size: 1.4rem;
  color: ${(props) => props.theme.colors.secondary};
  margin: 20px 0;
`;

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const CompaniesWrapper = styled.div`
  overflow-x: hidden;
  overflow-y: visible; /* allow vertical expansion */
  width: 100%;
  padding: 14px 0; /* prevents top/bottom clipping */
`;

const CompaniesTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${scroll} 30s linear infinite;

  &:hover {
    animation-play-state: paused;
  }
`;

const CompanyCard = styled.div`
  min-width: 160px;
  height: 90px;
  margin: 0 12px;
  border: 1px solid ${(props) => props.theme.colors.secondary};
  border-radius: 12px;
  background: ${(props) => props.theme.colors.light};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.04); /* slightly reduced for safety */
  }
`;


const CompanyLogo = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: grayscale(100%);
  transition: filter 0.3s ease;

  ${CompanyCard}:hover & {
    filter: grayscale(0%);
  }
`;


/* -------------------- CountUp Hook -------------------- */

const useCountUp = (target, start) => {
  const [count, setCount] = useState(0);



  useEffect(() => {
    if (!start) return;

    let current = 0;
    const duration = 1200;
    const step = target / (duration / 16);

    const timer = setInterval(() => {
      current += step;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [start, target]);

  return count;
};

const AnimatedStat = ({ value, suffix, label, start }) => {
  const count = useCountUp(value, start);

  return (
    <StatCard>
      <StatNumber>
        {count}
        {suffix}
      </StatNumber>
      <StatLabel>{label}</StatLabel>
    </StatCard>
  );
};

/* -------------------- Main Component -------------------- */

const Placement = () => {
  const statsRef = useRef(null);
  const [startAnimation, setStartAnimation] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartAnimation(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    if (statsRef.current) observer.observe(statsRef.current);
  }, []);

  const stats = [
    { value: 95, suffix: "%", label: "Placement Rate" },
    { value: 50, suffix: "+", label: "Hiring Companies" },
    { value: 200, suffix: "+", label: "Students Placed" },
  ];

  const companies = [
    "https://cdn.shopify.com/s/files/1/0851/4607/5431/files/1_60f5fb4b-1d3b-4c90-86dc-9b8c3373e05c.png?v=1767807406",
    "https://cdn.shopify.com/s/files/1/0851/4607/5431/files/5_666db4e8-e44c-4077-8dac-61f93deb1afc.png?v=1767807406",
    "https://cdn.shopify.com/s/files/1/0851/4607/5431/files/3_0387c640-c87b-4ffe-84d7-485455496e95.png?v=1767807406",
    "https://cdn.shopify.com/s/files/1/0851/4607/5431/files/2_8bcab1fa-f908-4bc1-ab48-0feb8848b7d2.png?v=1767807405",
    "https://cdn.shopify.com/s/files/1/0851/4607/5431/files/Accenture-Symbol.png?v=1767754911",
    "https://cdn.shopify.com/s/files/1/0851/4607/5431/files/Untitled_design_3.png?v=1767807652",
  ];

  return (
    <>
      <Helmet>
        <title>Placements</title>
        <meta
          name="description"
          content="Explore our placement achievements and top hiring companies."
        />
      </Helmet>

      <Section>
        <Heading>Our Placements</Heading>
        <SubHeading>
          Our students are trained with industry-focused skills that lead to
          real-world job opportunities.
        </SubHeading>

        {/* Stats */}
        <StatsGrid>
          {placementStats.map((stat, index) => (
            <StatCard
              as={motion.div}
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <StatNumber>
                <CountUp
                  end={stat.number}
                  duration={2}
                  enableScrollSpy
                />
                {stat.label.includes("%") ? "%" : "+"}
              </StatNumber>

              <StatLabel>{stat.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>


        {/* Companies */}
        <CompaniesHeading>Top Hiring Companies</CompaniesHeading>
        <CompaniesWrapper>
          <CompaniesTrack>
            {[...companies, ...companies].map((logo, idx) => (
              <CompanyCard key={idx}>
                <CompanyLogo src={logo} alt="Hiring Company" />
              </CompanyCard>
            ))}
          </CompaniesTrack>
        </CompaniesWrapper>
      </Section>
    </>
  );
};

export default Placement;
